import { TextLink } from 'cpool-components/general/TextLink';
import { FilterSelect } from 'cpool-components/input/FilterSelect';
import { LOAN_REQUEST_OFFER_STATUS } from 'cpool-constants';
import type { Schema_OfferFilterCriteria, Schema_OfferFilterStatus } from 'cpool-schemas';
import { objectKeys } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuBookmark, LuHeart, LuX } from 'react-icons/lu';

import type { Schema_AcceptedOfferCounts } from './hooks/useAcceptedOfferCounts';
import TreatButton from '~/components/TreatButton';
import { ROLES } from '~/constants';
import { cn } from '~/utils/cnTailwind';

const FILTER_ROLES = [ROLES.adviser, ROLES.team_leader, ROLES.corporation_director];

type Props = {
    counts: Schema_AcceptedOfferCounts;
    getCriteriaFilter: () => Schema_OfferFilterCriteria;
    getStatusFilter: () => Schema_OfferFilterStatus;
    setCriteriaFilter: (filter: Schema_OfferFilterCriteria) => void;
    setStatusFilter: (filter: Schema_OfferFilterStatus) => void;
};

export const OfferManagementFilter: React.FC<Props> = ({
    counts,
    getCriteriaFilter,
    getStatusFilter,
    setCriteriaFilter,
    setStatusFilter,
}) => {
    const { t } = useTranslation();
    const criteriaFilter = getCriteriaFilter();
    const statusFilter = getStatusFilter();
    const hasActiveFilters =
        statusFilter.length > 0 || Boolean(criteriaFilter.favored) || Boolean(criteriaFilter.teaser);

    const statusOptions = objectKeys(LOAN_REQUEST_OFFER_STATUS).map((status) => ({
        label: (
            <span className="flex items-center gap-2">
                <span>{t(`OFFER_MANAGEMENT.STATUS.${status}`)}</span>
                <span className="text-10 text-grey">{counts[status] ?? 0}</span>
            </span>
        ),
        value: status,
    }));

    const toggleCriteria = (key: 'favored' | 'teaser') =>
        setCriteriaFilter({ ...criteriaFilter, [key]: criteriaFilter[key] ? undefined : true });

    const chipClassName = (active: boolean | undefined) =>
        cn(
            'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-full border border-grey-outline-light',
            'bg-surface-light px-2.5 text-sm font-medium text-midnight transition-colors select-none',
            'hover:bg-grey-outline-light',
            active && 'border-transparent bg-midnight text-white hover:bg-midnight-hover hover:text-white',
        );

    return (
        // mb-4 on top of the py-2 above matches the 24px the design leaves between this row and the table
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 py-2">
            <FilterSelect
                placeholder={t('OFFER_MANAGEMENT.FILTERS.STATUS')}
                options={statusOptions}
                value={statusFilter}
                onChange={(selected) => setStatusFilter(selected as Schema_OfferFilterStatus)}
            />
            <div className="flex flex-wrap items-center gap-2">
                {hasActiveFilters && (
                    <TextLink
                        className="flex items-center gap-2 px-2 py-1.5 text-sm whitespace-nowrap hover:text-midnight-hover duration-200 select-none"
                        // An empty criteria filter also clears the status filter. A second navigation restores the stale criteria.
                        onClick={() => setCriteriaFilter({})}
                    >
                        {t('OFFER_MANAGEMENT.FILTERS.CLEAR')}
                        <LuX />
                    </TextLink>
                )}
                <TreatButton
                    roles={FILTER_ROLES}
                    className={chipClassName(criteriaFilter.favored)}
                    onClick={() => toggleCriteria('favored')}
                >
                    <span>{t('OFFER_MANAGEMENT.FILTERS.FAVORITE')}</span>
                    <LuHeart className="h-3.5 w-3.5" />
                </TreatButton>
                <TreatButton
                    roles={FILTER_ROLES}
                    className={chipClassName(criteriaFilter.teaser)}
                    onClick={() => toggleCriteria('teaser')}
                >
                    <span>{t('OFFER_MANAGEMENT.FILTERS.TEASER')}</span>
                    <LuBookmark className="h-3.5 w-3.5" />
                </TreatButton>
            </div>
        </div>
    );
};
